/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-nested-ternary */
import { Card, Grid, Step, StepLabel, Stepper } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MDTypography from "components/MDTypography";
import { fetchColors } from "apis";
import useUserPilot from "hooks/useUserPilot";
import { enqueueSnackbar } from "notistack";
import { RECURRENCE_TYPES } from "lib/constants";
import {
  templateAdd,
  getTemplateById,
  updateTemplate,
  fetchUserAllTemplate,
  updateUserSpecificTemplate,
  fetchUserSpecificTemplate,
  fetchUsers,
  fetchRecurrenceTypes,
} from "apis/coachup";
import { orderBy } from "lodash";
import Loader from "utils/Loader";
import { useSelector } from "react-redux";
import Cadence from "./TemplateFormSteps/Cadence";
import Questions from "./TemplateFormSteps/Questions";
import TemplateInfo from "./TemplateFormSteps/TemplateInfo";
import "../../../../styles/templates.scss";
import AssignTo from "./TemplateFormSteps/AssignTo";
import validationSchema, { validationSchemaForUserSpecific } from "../Validations";
import SelectTemplate from "./TemplateFormSteps/SelectTemplate";

function getSteps(userSpecificTemplateEdit) {
  if (userSpecificTemplateEdit) {
    return ["Select Template", "Cadence", "Questions"];
  }
  return ["Template Info", "Cadence", "Questions", "Assign To"];
}

function getStepContent(
  stepIndex,
  formData,
  colors,
  colorsLoading,
  users,
  usersLoading,
  templateData,
  recurrenceTypes,
  recurrenceTypesLoading
) {
  switch (stepIndex) {
    case 0:
      return <TemplateInfo formData={formData} colors={colors} colorsLoading={colorsLoading} />;
    case 1:
      return (
        <Cadence
          formData={formData}
          recurrenceTypes={recurrenceTypes}
          recurrenceTypesLoading={recurrenceTypesLoading}
        />
      );
    case 2:
      return <Questions formData={formData} />;
    case 3:
      return (
        <AssignTo
          formData={formData}
          users={users}
          usersLoading={usersLoading}
          templateData={templateData}
        />
      );
    default:
      return null;
  }
}

function getStepContentForUserSpecificEdit(
  stepIndex,
  formData,
  templates,
  templatesLoading,
  recurrenceTypes,
  recurrenceTypesLoading
) {
  switch (stepIndex) {
    case 0:
      return (
        <SelectTemplate
          templates={templates}
          templatesLoading={templatesLoading}
          formData={formData}
        />
      );
    case 1:
      return (
        <Cadence
          formData={formData}
          recurrenceTypes={recurrenceTypes}
          recurrenceTypesLoading={recurrenceTypesLoading}
        />
      );
    case 2:
      return <Questions formData={formData} />;
    default:
      return null;
  }
}

function TemplateForm() {
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [template, setTemplate] = useState();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState();
  const [initialValues, setInitialValues] = useState({
    info: {
      name: "",
      color: "",
      description: "",
      visiblity: "Private",
    },
    cadence: {
      type: "",
      days: "",
      dueDateOption: "",
      dueDateNum: "",
      monthOfYear: "",
      dayOfMonth: "",
      dueDate: "",
    },
    questions: [
      {
        question: "",
        type: "",
        childQuestion: "",
      },
    ],
    assignTo: [],
  });
  const [colors, setColors] = useState([]);
  const [recurrenceTypes, setRecurrenceTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [userName, setUserName] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [templateFetchLoading, setTemplateFetchLoading] = useState(false);
  const [userTemplateFetchLoading, setUserTemplateFetchLoading] = useState(false);
  const [templateData, setTemplateData] = useState();
  const [colorsLoading, setColorsLoading] = useState(false);
  const [recurrenceTypesLoading, setRecurrenceTypesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const location = useLocation();
  const { id } = useParams();
  const userSpecificTemplateEdit = location.pathname.includes("user");
  const steps = getSteps(userSpecificTemplateEdit);
  const currentValidation = userSpecificTemplateEdit
    ? validationSchemaForUserSpecific[activeStep]
    : validationSchema[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const Userpilot = useUserPilot();

  const getColors = async () => {
    try {
      setColorsLoading(true);
      const response = await fetchColors();
      if (response?.data?.success) {
        setColors(response.data.data.map((color) => color.color));
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setColorsLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const getRecurrenceTypes = async () => {
    try {
      setRecurrenceTypesLoading(true);
      const response = await fetchRecurrenceTypes();
      if (response?.data?.success) {
        const recurrenceTypesArr = response.data.data.map((recurrence) => ({
          label: recurrence?.displayName,
          value: recurrence?.recurrence,
        }));

        const orderedRecurrenceTypesArr = [];

        Object.keys(RECURRENCE_TYPES).forEach((type) => {
          const foundType = recurrenceTypesArr?.find(
            (item) => item.value === RECURRENCE_TYPES?.[type]
          );
          if (foundType) {
            orderedRecurrenceTypesArr.push(foundType);
          }
        });

        const remainingRecurrenceTypes = recurrenceTypesArr.filter(
          (recurrence) => !orderedRecurrenceTypesArr.some((item) => item.value === recurrence.value)
        );

        setRecurrenceTypes(orderedRecurrenceTypesArr.concat(remainingRecurrenceTypes));
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setRecurrenceTypesLoading(false);
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const getUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await fetchUsers();
      if (response?.data?.success) {
        setUsers(response?.data?.data?.filter((user) => user.id !== loginUser.id));
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setUsersLoading(false);
    } catch (error) {
      setUsersLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const formatQuestionAndRecurrenceDetails = (data) => {
    let recurrenceDetails = {};
    const questionsDetails = [];
    const assignTo = [];
    recurrenceDetails = {
      days: "",
      type: data?.recurrenceType,
      dueDateNum: data?.dueDateNum,
      dueDateOption: data?.dueDateOption,
      dayOfMonth: "",
      monthOfYear: "",
    };
    if (data?.recurrenceType === RECURRENCE_TYPES.WEEKLY) {
      recurrenceDetails.days = data?.CoachupRecurrenceDetail?.dayOfWeek;
    } else if (data?.recurrenceType === RECURRENCE_TYPES.MONTHLY) {
      recurrenceDetails.days = data?.CoachupRecurrenceDetail?.dayOfMonth;
    } else if (data?.recurrenceType === RECURRENCE_TYPES.QUARTERLY) {
      recurrenceDetails.days = data?.CoachupRecurrenceDetail?.daysBeforeQuarter;
    } else if (data?.recurrenceType === RECURRENCE_TYPES.ANNUALLY) {
      recurrenceDetails.monthOfYear = data?.CoachupRecurrenceDetail?.monthOfYear;
      recurrenceDetails.dayOfMonth = data?.CoachupRecurrenceDetail?.dayOfMonth;
    }
    let BEQuestion = data?.CoachupTemplateQuestions;
    BEQuestion = orderBy(BEQuestion, ["sortOrder"], ["asc"]);

    if (userSpecificTemplateEdit) {
      BEQuestion?.forEach((question) => {
        const structuredQuestion = {
          questionId: question?.id,
          question: question?.question,
          type: question?.answerType,
          childQuestion: "",
        };

        if (question?.answerType === "pipeline health") {
          structuredQuestion.childQuestion = question?.childQuestionDetail?.question;
          structuredQuestion.childQuestionId = question?.childQuestionDetail?.id;
          structuredQuestion.childQuestionType = "currency";
        }

        questionsDetails.push(structuredQuestion);
      });
    } else {
      BEQuestion?.forEach((question) => {
        const structuredQuestion = {
          questionId: question?.questionId,
          question: question?.CoachupQuestion?.question,
          type: question?.CoachupQuestion?.answerType,
          childQuestion: "",
        };

        if (question?.CoachupQuestion?.answerType === "pipeline health") {
          structuredQuestion.childQuestion =
            question?.CoachupQuestion?.childQuestionDetail?.question;
          structuredQuestion.childQuestionId = question?.CoachupQuestion?.childQuestionDetail?.id;
          structuredQuestion.childQuestionType = "currency";
        }

        questionsDetails.push(structuredQuestion);
      });
    }

    data?.CoachupUserTemplates?.forEach((user) => {
      if (user?.assignedBy === loginUser?.id) {
        assignTo.push(user.userId);
      }
    });

    return { cadence: recurrenceDetails, questions: questionsDetails, assignTo };
  };

  const formatBEResponse = (data) => {
    const formattedData = {
      questions: [],
      assignTo: [],
    };
    if (!userSpecificTemplateEdit && id && location?.pathname?.includes("edit")) {
      formattedData.info = {
        name: data?.name,
        color: data?.color,
        description: data?.description,
        visiblity: !data?.userId ? "Public" : "Private",
        recurrenceDetailsId: data?.recurrenceDetailsId,
      };

      const { cadence, questions, assignTo } = formatQuestionAndRecurrenceDetails(data);
      formattedData.cadence = cadence;
      formattedData.questions = questions;
      formattedData.assignTo = assignTo;
    }
    if (id && location?.pathname?.includes("copy")) {
      formattedData.info = {
        name: "",
        color: data?.color,
        description: data?.description,
        visiblity: !data.userId ? "Public" : "Private",
      };
      const { cadence, questions } = formatQuestionAndRecurrenceDetails(data);
      formattedData.cadence = cadence;
      formattedData.questions = questions;
      formattedData.assignTo = [];
    }

    if (userSpecificTemplateEdit) {
      const { cadence, questions } = formatQuestionAndRecurrenceDetails(data);
      formattedData.cadence = cadence;
      formattedData.questions = questions;
      formattedData.assignTo = [];
    }

    return formattedData;
  };

  const getTemplate = async (chosenTemplate) => {
    try {
      setTemplateFetchLoading(true);
      const response = await getTemplateById(chosenTemplate?.id || id, {
        edit: location.pathname.includes("edit") && true,
      });
      if (response?.data?.success) {
        const formattedTemplate = formatBEResponse(response.data.data);
        setTemplate((prev) => ({ ...prev, ...formattedTemplate }));
        setTemplateData(response?.data?.data);
        if (userSpecificTemplateEdit) {
          setSelectedTemplate(chosenTemplate);
          setInitialValues((prev) => ({
            ...prev,
            ...formattedTemplate,
            template: chosenTemplate,
          }));
        } else {
          setInitialValues((prev) => ({ ...prev, ...formattedTemplate }));
        }
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );

        if (response?.response?.status === 404) {
          navigate(-1);
        }
      }
      setTemplateFetchLoading(false);
    } catch (error) {
      console.log(error);
      setTemplateFetchLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const getUserAllTemplate = async () => {
    try {
      setUserTemplateFetchLoading(true);
      const response = await fetchUserAllTemplate(id);
      if (response?.data?.success) {
        setTemplates(response.data.data.templates);
        setUserName(response.data.data.userName);
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );

        if (response?.response?.status === 404) {
          navigate(-1);
        }
      }
      setUserTemplateFetchLoading(false);
    } catch (error) {
      console.log(error);
      setUserTemplateFetchLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const getUserSpecificTemplate = async (chosenTemplate) => {
    try {
      setTemplateFetchLoading(true);
      const response = await fetchUserSpecificTemplate(id, { templateId: chosenTemplate?.id });
      if (response?.data?.success) {
        const formattedTemplate = formatBEResponse({
          ...response.data.data,
          CoachupTemplateQuestions: response?.data?.data?.CoachupTemplate?.CoachupQuestions,
        });
        setTemplate((prev) => ({ ...prev, ...formattedTemplate }));
        if (userSpecificTemplateEdit) {
          setSelectedTemplate(chosenTemplate);
          setInitialValues((prev) => ({
            ...prev,
            ...formattedTemplate,
            template: chosenTemplate,
          }));
        } else {
          setInitialValues((prev) => ({ ...prev, ...formattedTemplate }));
        }
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );

        if (response?.response?.status === 404) {
          navigate(-1);
        }
      }
      setTemplateFetchLoading(false);
    } catch (error) {
      console.log(error);
      setTemplateFetchLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (id && !location.pathname.includes("user")) {
      getTemplate();
    }
    if (userSpecificTemplateEdit) {
      getUserAllTemplate();
    }
  }, [id]);

  useEffect(() => {
    if (userSpecificTemplateEdit) {
      setInitialValues((prevState) => ({
        ...prevState,
        template: selectedTemplate || null,
      }));
    } else {
      getColors();
      getUsers();
    }
    getRecurrenceTypes();
  }, []);

  const handleBack = () => setActiveStep(activeStep - 1);

  const generatePayloadFromFormData = (data) => {
    const payload = {
      template: {
        name: data?.info?.name,
        description: data?.info?.description,
        color: data?.info?.color,
        visibility: data?.info?.visiblity?.toLowerCase(),
        recurrenceType: data?.cadence?.type,
        recurrenceData: {},
        dueDateNum: data?.cadence?.dueDateNum,
        dueDateOption: data?.cadence?.dueDateOption,
        recurrenceDetailsId: data?.info?.recurrenceDetailsId,
      },
      questionList: [],
      assignmentList: [],
    };

    if (data.cadence.type === RECURRENCE_TYPES.NO_RECURRENCE) {
      delete payload.template.recurrenceData;
    } else if (data.cadence.type === RECURRENCE_TYPES.WEEKLY) {
      payload.template.recurrenceData = {
        dayOfWeek: data.cadence.days,
      };
    } else if (data.cadence.type === RECURRENCE_TYPES.MONTHLY) {
      payload.template.recurrenceData = {
        dayOfMonth: data.cadence.days,
      };
    } else if (data.cadence.type === RECURRENCE_TYPES.QUARTERLY) {
      payload.template.recurrenceData = {
        daysBeforeQuarter: data.cadence.days,
      };
    } else if (data.cadence.type === RECURRENCE_TYPES.ANNUALLY) {
      payload.template.recurrenceData = {
        monthOfYear: data.cadence.monthOfYear,
        dayOfMonth: data.cadence.dayOfMonth,
      };
    }

    data?.questions?.forEach((question, index) => {
      const structuredQuestion = {
        id: (!location.pathname.includes("copy") && question?.questionId) || undefined,
        question: question?.question,
        answerType: question?.type,
        sortOrder: index + 1,
      };
      if (question.type === "pipeline health") {
        structuredQuestion.child = {
          question: question.childQuestion,
          answerType: question.childQuestionType,
          id: (!location.pathname.includes("copy") && question.childQuestionId) || undefined,
        };
      }
      payload?.questionList?.push(structuredQuestion);
    });

    data?.assignTo?.forEach((userId) => {
      payload.assignmentList.push({ id: userId });
    });

    return payload;
  };

  const handleAddTemplate = async (data, actions) => {
    try {
      setIsLoading(true);
      const response = await templateAdd(generatePayloadFromFormData(data));
      if (response?.data?.success) {
        Userpilot.track("Coachup Template Created", {
          coachTemplateName: data.info.name,
        });
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message || "Template Added Successfully",
          }),
          {
            variant: "custom",
          }
        );
        navigate("/coach/templates");
      } else if (response?.response?.status === 422) {
        setActiveStep(0);
        actions.setErrors({
          info: {
            name: "Template name already exists",
          },
        });
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleTemplateEdit = async (data, actions) => {
    try {
      const payload = generatePayloadFromFormData(data);

      const removeQuestionListIds = template.questions
        .filter(
          (BEQuestion) =>
            !payload.questionList.some(
              (question) => question.id && question.id === BEQuestion.questionId
            )
        )
        .map((question) => question.questionId);

      payload.removeQuestionListIds = [...removeQuestionListIds];

      const removeUserAssignIds = template.assignTo.filter(
        (userId) => !payload.assignmentList.some((user) => userId === user.id)
      );

      payload.removeUserAssignIds = [...removeUserAssignIds];
      payload.assignmentList = payload.assignmentList.filter(
        (user) => !template.assignTo.includes(user.id)
      );

      const response = await updateTemplate(payload, id);
      if (response?.data?.success) {
        Userpilot.track("Coachup Template Updated", {
          coachUpTemplateName: data.info.name,
        });
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message || "Template Updated Successfully",
          }),
          {
            variant: "custom",
          }
        );
        navigate("/coach/templates");
      } else if (response?.response?.status === 422) {
        setActiveStep(0);
        actions.setErrors({
          info: {
            name: "Template name already exists",
          },
        });
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleUserSpecificEdit = async (data) => {
    try {
      const payload = generatePayloadFromFormData(data);

      const formattedPayload = {
        templateId: selectedTemplate.id,
        dueDateNum: payload.template.dueDateNum,
        dueDateOption: payload.template.dueDateOption,
        recurrenceDetail: {
          recurrenceType: payload.template.recurrenceType,
          recurrenceData: payload.template.recurrenceData,
        },
        newQuestionList: payload.questionList,
        removeQuestionList: template.questions
          .filter(
            (BEQuestion) =>
              !payload.questionList.some(
                (question) => question.id && question.id === BEQuestion.questionId
              )
          )
          .map((question) => ({ id: question.questionId })),
      };

      const response = await updateUserSpecificTemplate(formattedPayload, id);
      if (response?.data?.success) {
        Userpilot.track("Coachup Template Updated", {
          coachUpTemplateName: data.info.name,
        });
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response?.data?.message || "Template Updated Successfully",
          }),
          {
            variant: "custom",
          }
        );
        navigate("/coach/templates");
      } else if (response?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const handleSubmit = async (data, actions) => {
    if (isLastStep) {
      if (!id || (id && location?.pathname?.includes("copy"))) {
        handleAddTemplate(data, actions);
      }
      if (!userSpecificTemplateEdit && id && location?.pathname?.includes("edit")) {
        handleTemplateEdit(data, actions);
      }
      if (userSpecificTemplateEdit) {
        handleUserSpecificEdit(data);
      }
    } else if (userSpecificTemplateEdit && activeStep === 0) {
      actions.setTouched({});
      if (selectedTemplate?.id !== data.template.id) {
        await getUserSpecificTemplate(data.template);
      }

      setActiveStep(activeStep + 1);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
    }
    setIsLoading(false);
  };

  return (
    <DashboardLayout>
      {/* header */}
      <DashboardNavbar />
      <MDBox py={3} className="template-form-wrapper">
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={12} lg={10}>
            <MDTypography variant="h4">
              {userSpecificTemplateEdit
                ? `Edit Template For ${userName || ""}`
                : location.pathname.includes("edit")
                ? "Edit Template"
                : location.pathname.includes("copy")
                ? "Copy Template"
                : "Add Template"}
            </MDTypography>
          </Grid>
        </Grid>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
          <Grid item xs={12} lg={10}>
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={currentValidation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <MDBox mx={2} mt={-3}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </MDBox>
                    <MDBox sx={{ position: "relative" }} p={3}>
                      <Loader loading={templateFetchLoading} />

                      <MDBox>
                        {userSpecificTemplateEdit
                          ? getStepContentForUserSpecificEdit(
                              activeStep,
                              {
                                values,
                                touched,
                                errors,
                                setFieldValue,
                              },
                              templates,
                              userTemplateFetchLoading,
                              recurrenceTypes,
                              recurrenceTypesLoading
                            )
                          : getStepContent(
                              activeStep,
                              {
                                values,
                                touched,
                                errors,
                                setFieldValue,
                              },
                              colors,
                              colorsLoading,
                              users,
                              usersLoading,
                              templateData,
                              recurrenceTypes,
                              recurrenceTypesLoading
                            )}
                        <MDBox mt={4} width="100%" display="flex" justifyContent="space-between">
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton variant="gradient" color="secondary" onClick={handleBack}>
                              PREV
                            </MDButton>
                          )}
                          <MDButton type="submit" color={isLastStep ? "secondaryGreen" : "gray80"}>
                            {isLastStep ? (isLoading ? "Loading" : "COMPLETE") : "NEXT"}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default TemplateForm;
